import { z } from 'zod'
import {
  idSchema,
  isIsoDate,
  isIsoInstant,
  reasonSchema,
  revisionSchema,
  text,
} from '../validators'
import { type EngagementStatus, MEETING_STATES, MEETING_TYPES, type MeetingState } from '../vocab'
import type { MinutesVersion } from './minutes'
import type { PersonRef, PersonView } from './review'

/**
 * Meetings (M7: P-CAL-001/-003, CAL-020..037, GAP-UI-014/015). Instants are UTC
 * (CAL-023); `timezone` is the organiser's display zone only. Nothing here ever
 * sends an invitation or contacts a company (CAL-067).
 */

/** Field limits shared by the API and the web forms (P-UI-008, GAP-UI-016). */
export const MEETING_LIMITS = {
  title: { min: 3, max: 200 },
  location: { min: 3, max: 300 },
  agenda: { min: 8, max: 6000 },
  companyParticipants: { min: 3, max: 2000 },
  objectives: { max: 20 },
  /** The calendar range query spans at most this many days (P-CAL-003). */
  rangeDays: 366,
} as const

export const MEETING_DEFAULT_LOCATION = 'Video conference'
export const MEETING_DEFAULT_PARTICIPANTS = 'Company representatives to be confirmed'

/** A UTC instant `YYYY-MM-DDTHH:mm:ss(.sss)?Z`, stored normalised to milliseconds (CAL-023). */
export const meetingInstantSchema = z
  .string()
  .refine(isIsoInstant, { message: 'Use a UTC time (YYYY-MM-DDTHH:mm:ssZ).' })
  .transform((v) => new Date(v).toISOString())

function isTimeZone(value: string): boolean {
  if (value === 'UTC') return true
  try {
    new Intl.DateTimeFormat('en-GB', { timeZone: value })
    return true
  } catch {
    return false
  }
}

/** IANA zone name used to display the schedule (e.g. `Europe/Oslo`). */
export const timeZoneSchema = z
  .string()
  .min(1)
  .max(64)
  .refine((v) => /^[A-Za-z0-9_+\-/]+$/.test(v) && isTimeZone(v), {
    message: 'Choose a valid time zone.',
  })

const objectiveIdsSchema = z
  .array(idSchema)
  .max(MEETING_LIMITS.objectives.max)
  .refine((ids) => new Set(ids).size === ids.length, { message: 'Objectives must be unique.' })

const scheduleFields = {
  title: text(MEETING_LIMITS.title.min, MEETING_LIMITS.title.max),
  meeting_type: z.enum(MEETING_TYPES.values),
  starts_at: meetingInstantSchema,
  ends_at: meetingInstantSchema,
  timezone: timeZoneSchema,
  location: text(MEETING_LIMITS.location.min, MEETING_LIMITS.location.max),
  agenda: text(MEETING_LIMITS.agenda.min, MEETING_LIMITS.agenda.max),
  company_participants: text(
    MEETING_LIMITS.companyParticipants.min,
    MEETING_LIMITS.companyParticipants.max,
  ),
  participation_cutoff: meetingInstantSchema.nullable(),
  objective_ids: objectiveIdsSchema,
}

/** `POST /meetings` (CAL-021): unknown fields are refused (422). */
export const meetingCreateSchema = z.strictObject({
  engagement_id: idSchema,
  title: scheduleFields.title,
  meeting_type: scheduleFields.meeting_type,
  starts_at: scheduleFields.starts_at,
  ends_at: scheduleFields.ends_at,
  timezone: scheduleFields.timezone.default('UTC'),
  location: scheduleFields.location.default(MEETING_DEFAULT_LOCATION),
  agenda: scheduleFields.agenda,
  company_participants: scheduleFields.company_participants.default(MEETING_DEFAULT_PARTICIPANTS),
  /** Defaults to `starts_at` (CAL-026). */
  participation_cutoff: scheduleFields.participation_cutoff.optional(),
  objective_ids: scheduleFields.objective_ids.default([]),
})
export type MeetingCreate = z.infer<typeof meetingCreateSchema>

/** `PATCH /meetings/:id` (CAL-029): drafts only, at least one field, never the engagement. */
export const meetingUpdateSchema = z
  .strictObject({
    revision: revisionSchema,
    title: scheduleFields.title.optional(),
    meeting_type: scheduleFields.meeting_type.optional(),
    starts_at: scheduleFields.starts_at.optional(),
    ends_at: scheduleFields.ends_at.optional(),
    timezone: scheduleFields.timezone.optional(),
    location: scheduleFields.location.optional(),
    agenda: scheduleFields.agenda.optional(),
    company_participants: scheduleFields.company_participants.optional(),
    participation_cutoff: scheduleFields.participation_cutoff.optional(),
    objective_ids: scheduleFields.objective_ids.optional(),
  })
  .refine((v) => Object.keys(v).some((k) => k !== 'revision'), {
    message: 'Change at least one field.',
  })
export type MeetingUpdate = z.infer<typeof meetingUpdateSchema>

export const meetingPublishSchema = z.strictObject({ revision: revisionSchema })
export type MeetingPublish = z.infer<typeof meetingPublishSchema>

/** Cancel a draft or published meeting with a reason (P-CAL-001). */
export const meetingCancelSchema = z.strictObject({
  revision: revisionSchema,
  reason: reasonSchema,
})
export type MeetingCancel = z.infer<typeof meetingCancelSchema>

/**
 * Reschedule a published meeting: the original becomes `superseded` and a new,
 * published meeting links back to it (P-CAL-001). Unset fields are copied.
 */
export const meetingRescheduleSchema = z.strictObject({
  revision: revisionSchema,
  reason: reasonSchema,
  starts_at: scheduleFields.starts_at,
  ends_at: scheduleFields.ends_at,
  timezone: scheduleFields.timezone.optional(),
  participation_cutoff: scheduleFields.participation_cutoff.optional(),
  title: scheduleFields.title.optional(),
  location: scheduleFields.location.optional(),
  agenda: scheduleFields.agenda.optional(),
})
export type MeetingReschedule = z.infer<typeof meetingRescheduleSchema>

function isRangeBound(value: string): boolean {
  return isIsoDate(value) || isIsoInstant(value)
}

/**
 * `GET /meetings` (P-CAL-003): a required range of at most a year, as dates
 * (inclusive) or UTC instants (`to` exclusive), plus filters and pagination.
 */
export const meetingListQuerySchema = z
  .object({
    from: z.string().refine(isRangeBound, { message: 'Use YYYY-MM-DD or a UTC time.' }),
    to: z.string().refine(isRangeBound, { message: 'Use YYYY-MM-DD or a UTC time.' }),
    engagement_id: idSchema.optional(),
    issuer_id: idSchema.optional(),
    state: z.enum(MEETING_STATES.values).optional(),
    /** Narrow a dual-role caller to the investor or staff view. */
    view: z.enum(['investor', 'staff']).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(200),
    cursor: z.string().max(1024).optional(),
  })
  .transform((v) => ({
    ...v,
    fromInstant: isIsoDate(v.from) ? `${v.from}T00:00:00.000Z` : new Date(v.from).toISOString(),
    toInstant: isIsoDate(v.to)
      ? new Date(Date.parse(`${v.to}T00:00:00.000Z`) + 86_400_000).toISOString()
      : new Date(v.to).toISOString(),
  }))
  .superRefine((v, ctx) => {
    const span = Date.parse(v.toInstant) - Date.parse(v.fromInstant)
    if (span <= 0) {
      ctx.addIssue({ code: 'custom', path: ['to'], message: 'The end must follow the start.' })
    } else if (span > MEETING_LIMITS.rangeDays * 86_400_000) {
      ctx.addIssue({ code: 'custom', path: ['to'], message: 'Choose a range of a year or less.' })
    }
  })
export type MeetingListQuery = z.infer<typeof meetingListQuerySchema>

export const meetingViewQuerySchema = z.object({
  view: z.enum(['investor', 'staff']).optional(),
})

// --- Response shapes -------------------------------------------------------

export const MINUTES_STATUSES = ['none', 'in_progress', 'published'] as const
export type MinutesStatus = (typeof MINUTES_STATUSES)[number]

/** Why an investor may not ask to take part (CAL-071). */
export const PARTICIPATION_BLOCKS = [
  'case_closed',
  'meeting_started',
  'cutoff_passed',
  'already_requested',
] as const
export type ParticipationBlock = (typeof PARTICIPATION_BLOCKS)[number]

export interface MeetingSummary {
  id: string
  title: string
  meeting_type: string
  starts_at: string
  ends_at: string
  timezone: string
  location: string
  state: MeetingState
  engagement: { id: string; title: string; status: EngagementStatus }
  company: { id: string; name: string }
  minutes_status: MinutesStatus
  supersedes_id: string | null
  superseded_by_id: string | null
}

/** A related objective as the meeting page prepares it: where it stands and what to test. */
export interface MeetingObjectiveBrief {
  id: string
  title: string
  /** Current stage: the latest published milestone (S0 when none). */
  stage: string
  change_request: string
  /** The latest published milestone's rationale: the commitment or evidence on record. */
  latest_evidence: string | null
  limitations: string | null
  next_action: string
  due_date: string
  owner: string | null
}

/** Meeting preparation: the case context to bring into the room (researcher request). */
export interface MeetingPreparation {
  objectives: MeetingObjectiveBrief[]
  /** Investor questions on this engagement (investors: their own, or their org's for admins). */
  questions: {
    id: string
    subject: string
    status: string
    asked_by: string | null
    org: string | null
    last_message_at: string
  }[]
  last_correspondence: {
    id: string
    title: string
    event_date: string
    event_type: string
    company_response: string
    fruuit_judgment: string
    state: string
  } | null
}

/**
 * Commitments recorded at or after this meeting: S2+ milestones on the related
 * objectives dated from a week before the meeting until the case's next meeting
 * (there is no direct milestone-to-meeting link column).
 */
export interface MeetingCommitment {
  milestone_id: string
  objective_id: string
  objective_title: string
  stage: string
  assessment_date: string
  rationale: string
  limitations: string
  state: string
}

export interface MeetingFollowUp {
  objective_id: string
  title: string
  next_action: string
  owner: string | null
  due_date: string
}

export interface MeetingDetail extends MeetingSummary {
  agenda: string
  company_participants: string
  participation_cutoff: string
  objectives: { id: string; title: string }[]
  /** Display name only for investors; staff also get the user id (P-ID-006). */
  manager: PersonView | null
  /** Staff only. */
  reviewer?: PersonRef | null
  author?: PersonRef | null
  published_at: string | null
  cancelled_at?: string | null
  cancel_reason?: string | null
  superseded_at?: string | null
  reschedule_reason?: string | null
  /** The meeting this one replaced, with the reason it moved. */
  supersedes: { id: string; starts_at: string; ends_at: string; reason: string | null } | null
  superseded_by: { id: string; starts_at: string } | null
  revision: number
  created_at?: string
  updated_at?: string
  /** Visible minutes versions, newest first (investors: published only). */
  minutes: MinutesVersion[]
  preparation: MeetingPreparation
  commitments: MeetingCommitment[]
  follow_up: MeetingFollowUp[]
  /** Investors: server-side participation eligibility (CAL-071). */
  participation?: { can_request: boolean; reason: ParticipationBlock | null }
  /** Staff: what the caller may do now (cosmetic; the server enforces, P-UI-003). */
  permissions?: {
    edit: boolean
    publish: boolean
    cancel: boolean
    reschedule: boolean
    add_minutes: boolean
  }
}

export interface MeetingPage {
  items: MeetingSummary[]
  next_cursor: string | null
  timezone: 'UTC'
  server_time: string
  view: 'investor' | 'staff'
}
